import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Trophy, Users, UserCircle, Calendar, LogOut,
  ChevronLeft, Menu, Home, Shield, Crown, FileSpreadsheet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import axios from 'axios';
import { API } from '@/App';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/f617a5a9-dda7-40dc-bbb9-de762658adaf/images/5d32d876981551b4c98ceec76bfdad1e26bc99093c09f2a5f2242b603b114d79.png";

export const AdminSidebar = ({ user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const isSuperAdmin = user?.admin_type === 'super_admin';

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Leagues', path: '/admin/leagues', icon: Trophy },
    { name: 'Teams', path: '/admin/teams', icon: Users },
    { name: 'Players', path: '/admin/players', icon: UserCircle },
    { name: 'Matches', path: '/admin/matches', icon: Calendar },
    { name: 'Manage Users', path: '/admin/manage-users', icon: Shield },
    { name: 'Bulk Data', path: '/admin/bulk-data', icon: FileSpreadsheet },
    ...(isSuperAdmin ? [{ name: 'Manage Admins', path: '/admin/manage-admins', icon: Crown, superAdminOnly: true }] : []),
  ];

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) {}
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="CricORama" className="w-8 h-8 object-contain" />
          <span className="font-bold text-lg text-[#2D5A5A] font-display">Admin</span>
        </Link>
        <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)}>
          <Menu className="w-5 h-5" />
        </Button>
      </div>

      {/* Mobile Overlay */}
      {collapsed && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setCollapsed(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-50
        transition-transform duration-300 w-64
        ${collapsed ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="CricORama" className="w-10 h-10 object-contain" />
              <div>
                <span className="font-bold text-lg text-slate-900 font-display">CricORama</span>
                <span className="block text-xs text-slate-500">Admin</span>
              </div>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setCollapsed(false)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setCollapsed(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors
                    ${isActive 
                      ? item.superAdminOnly 
                        ? 'bg-[#8B1E3F] text-white shadow-lg shadow-[#8B1E3F]/20'
                        : 'bg-[#2D5A5A] text-white shadow-lg shadow-[#2D5A5A]/20' 
                      : item.superAdminOnly
                        ? 'text-[#8B1E3F] hover:bg-[#8B1E3F]/10'
                        : 'text-slate-600 hover:bg-slate-100'}
                  `}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                  {item.superAdminOnly && (
                    <span className="ml-auto text-xs bg-[#8B1E3F]/20 px-2 py-0.5 rounded-full">Super</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.profile_image_url} />
                <AvatarFallback className="bg-[#2D5A5A] text-white">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-1" /> Site
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
